import type {
  FormItemNode,
  IDocumentNode,
  PartialNode,
} from "@shared/common/node/type";
import type {
  OnFormStateChange,
  OnFormSubmit,
  OnFormUpload,
} from "@shared/common/types/form";
import { getContext, setContext } from "svelte";
import { writable, type Writable } from "svelte/store";
import type { Store } from "./store";

/** Form Context */

export type CheckBoxMapItem = {
  required: boolean;
  list: Set<HTMLInputElement>;
  min?: number;
  max?: number;
};

export type FormContext = {
  onSubmit: OnFormSubmit;
  onUpload: OnFormUpload;
  onStateChange: OnFormStateChange;
  checkboxMap: Map<string, CheckBoxMapItem>;
  itemNodeMap: Map<string, PartialNode<FormItemNode>>;
  addCheckbox: (args: CheckboxStoreAddArgs) => void;
  removeCheckbox: (args: CheckboxStoreAddArgs) => void;
  checkCustomValidity: (formElement: HTMLFormElement) => boolean;
  width: Writable<number>;
};

type CheckboxStoreAddArgs = {
  name: string;
  element: HTMLInputElement;
};

export const getOrInitCheckboxItem = (
  checkboxMap: Map<string, CheckBoxMapItem>,
  name: string
): CheckBoxMapItem => {
  let item = checkboxMap.get(name);
  if (!item) {
    item = {
      required: false,
      list: new Set(),
    };
    checkboxMap.set(name, item);
  }
  return item;
};

export const setFormContext = (
  args: Pick<FormContext, "onSubmit" | "onUpload" | "onStateChange">
) => {
  const checkboxMap = new Map<string, CheckBoxMapItem>();
  const itemNodeMap = new Map<
    /* node label */ string,
    PartialNode<FormItemNode>
  >();

  return setContext<FormContext>("form", {
    ...args,
    checkboxMap,
    itemNodeMap,
    width: writable(0),

    addCheckbox: (args: CheckboxStoreAddArgs) => {
      const { name, element } = args;
      getOrInitCheckboxItem(checkboxMap, name).list.add(element);
    },

    removeCheckbox: (args: CheckboxStoreAddArgs) => {
      const { name, element } = args;
      getOrInitCheckboxItem(checkboxMap, name).list.delete(element);
    },

    checkCustomValidity: (formElement: HTMLFormElement) => {
      for (const { list, required, max } of checkboxMap.values()) {
        const inputs = [...list.values()];
        const lastInput = inputs.at(-1);

        if (required && inputs.every((e) => !e.checked)) {
          lastInput?.setCustomValidity("필수 항목입니다.");
        } else if (
          typeof max === "number" &&
          inputs.filter((e) => e.checked).length > max
        ) {
          lastInput?.setCustomValidity(`최대 ${max}개까지 선택 가능합니다.`);
        } else {
          lastInput?.setCustomValidity("");
        }
      }
      return formElement.checkValidity();
    },
  });
};

export const getFormContext = () => {
  return getContext<FormContext>("form");
};

/** Document Node Context */

// TODO: 현재 document 에서도 호출하고 slot과 swiper-slide 에서도 호출하고 있음. 나뉘어져 있는 이유 불명. 추후 수정
export const setDocumentNodeContext = (
  initialContext: PartialNode<IDocumentNode>
) => {
  return setContext("documentNode", initialContext);
};

export const getCurrentDocumentNode = () => {
  return getContext<PartialNode<IDocumentNode>>("documentNode");
};

export const setIsChildDocument = (isChildDocument: boolean) => {
  return setContext<boolean>("isChildDocument", isChildDocument);
};

export const getIsChildDocument = () => {
  return getContext<boolean>("isChildDocument");
};

/** Store */

export const setStore = (store: Store) => {
  return setContext("store", store);
};

export const getStore = () => {
  return getContext<Store>("store");
};

/** Slide Node Index Context */

export const setSlideNodeIndexContext = (index: number) => {
  return setContext<number>("slideNodeIndex", index);
};

export const getSlideNodeIndexContext = () => {
  return getContext<number>("slideNodeIndex");
};
